import React, { useState, useRef, useEffect, useCallback } from "react"
import { makeStyles } from "@material-ui/core/styles"
import { Box } from "@material-ui/core"
import ReactPlayer from "react-player"
import screenfull from "screenfull"
import PropTypes from "prop-types"
import Module from "../components/Module"
import Text from "../components/Text"
import Icon from "../components/Icon"
import { useTranslation } from "react-i18next"
import clsx from "clsx"
import PlayButton from "../components/PlayButton"

import { Swiper, SwiperSlide } from "swiper/react"
import SwiperCore, { Navigation, Pagination } from "swiper"

SwiperCore.use([Navigation, Pagination])

const useStyles = makeStyles(theme => ({
  gallery: {
    fontFamily: "Spezia, 'Roboto', 'Open Sans', Helvetica, Arial, sans-serif",
    padding: "32px 28px",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      padding: "24px 16px",
    },
    "& .icon": {
      width: 32,
      height: 32,
      [theme.breakpoints.down("sm")]: {
        width: 24,
        height: 24,
      },
    },
  },
  title: {
    color: "black",
    textAlign: "center",
    marginBottom: 32,
    [theme.breakpoints.down("sm")]: {
      marginBottom: 24,
    },
  },
  selectContainer: {
    width: "fit-content",
    margin: "0 auto 32px",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      marginBottom: 24,
    },
  },
  select: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    height: 40,
    padding: "20px 16px 16px",
    backgroundColor: "#fbfbfb",
    color: "#808080",
    fontSize: 16,
    cursor: "pointer",
    border: "none !important",
    maxWidth: 335,
    gap: 8,
    "& span": {
      maxWidth: 335,
      width: "100%",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      overflow: "hidden",
    },
  },
  selectArrow: {
    border: "solid #ef901c",
    borderWidth: "0 2px 2px 0",
    display: "inline-block",
    padding: 5,
    transform: props => (props.openSelect ? "rotate(225deg)" : "rotate(45deg)"),
    position: props => (!props.openSelect ? "relative" : "static"),
    top: props => (!props.openSelect ? "-6px" : "auto"),
  },
  selectOptions: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 10,
    marginTop: 15,
    width: "100%",
    backgroundColor: "#fbfbfb",
    border: "1px solid #ef901c",
    cursor: "pointer",
    padding: 8,
  },
  selectOption: {
    maxWidth: 335,
    width: "100%",
    margin: "0 auto",
    color: "#808080",
    minHeight: 25,
    height: "100%",
    textAlign: "center",
    wordBreak: "break-word",
    "&:not(:last-child)": {
      marginBottom: 8,
    },
    "&:hover": {
      color: "#ef901c",
    },
  },
  activeSlideTitle: {
    fontSize: 18,
    fontWeight: 600,
    color: "#494949",
    textAlign: "center",
    marginBottom: 24,
    minHeight: 30,
    [theme.breakpoints.down("sm")]: {
      fontSize: 16,
      marginBottom: 0,
    },
  },
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: 0,
    },
  },
  singleVideo: {
    maxWidth: "40%",
    margin: "0 auto",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
    },
    "& $playerWrapper": {
      paddingTop: "56.25%",
    },
  },
  swiper: {
    width: "100%",
    padding: "40px 0 !important",
    [theme.breakpoints.down("sm")]: {
      padding: "0 !important",
    },
  },
  slide: {
    transition: "transform 0.4s ease-in-out, opacity 0.4s ease-in-out",
    height: "auto",
    "&:not(.swiper-slide-active)": {
      [theme.breakpoints.up("md")]: {
        transform: "scale(0.6) !important",
        opacity: 0.5,
      },
    },
    "&.swiper-slide-active": {
      [theme.breakpoints.up("md")]: {
        minWidth: "30%",
        transform: "scale(1.3) !important",
        opacity: 1,
        zIndex: 1,
      },
    },
  },
  playerWrapper: {
    position: "relative",
    width: "100%",
    paddingTop: "56.25%",
    overflow: "hidden",
    background: "#000",
    "& .react-player__preview": {
      "& .react-player__play-icon": {
        display: "none !important",
      },
    },
  },
  slideOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    zIndex: 2,
  },
  thumbnailImg: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    "& > div": {
      width: "100%",
      height: "100%",
      backgroundSize: "cover !important",
    },
  },
  playButtonOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    cursor: "pointer",
    zIndex: 1,
  },
  videoFrame: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100% !important",
    height: "100% !important",
  },
  navButton: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    zIndex: 10,
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "&$prev": {
      marginRight: 16,
    },
    "&$next": {
      marginLeft: 16,
    },
    [theme.breakpoints.down("md")]: {
      display: "none !important",
    },
  },
  unmuteButton: {
    position: "absolute",
    width: 32,
    height: 32,
    bottom: 16,
    right: 16,
    cursor: "pointer",
    zIndex: 3,
  },
  prev: {},
  next: {},
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 8,
    marginTop: 24,
    "& .swiper-pagination-bullet": {
      borderRadius: "50%",
      cursor: "pointer",
      transition: "background-color 0.3s ease",
      width: 12,
      height: 12,
      textAlign: "center",
      lineHeight: "20px",
      fontSize: 12,
      color: "#000",
      opacity: 1,
      background: "#d2d3d6",
      "&-active": {
        backgroundColor: "#ef901c",
        transform: "scale(1)",
      },
    },
  },
}))

const VideoPlayerContent = React.memo(
  ({
    videoData,
    isPlaying,
    isMuted,
    onPlayClick,
    onPlay,
    onPause,
    onTogglePlay,
    autoStartMuted,
    onMute,
    classes,
  }) => (
    <div className={classes.playerWrapper}>
      {isPlaying && (
        <div className={classes.slideOverlay} onClick={onTogglePlay} />
      )}

      {isPlaying ? (
        <>
          <ReactPlayer
            muted={isMuted}
            playsinline={true}
            playing={isPlaying}
            controls={false}
            className={classes.videoFrame}
            url={videoData?.field_value?.link_video?.field_value}
            onPause={onPause}
            onPlay={onPlay}
            width="100%"
            height="100%"
            config={{
              youtube: {
                playerVars: {
                  modestbranding: 1,
                  rel: 0,
                  controls: 0,
                  loop: 1,
                  showinfo: 0,
                  disablekb: 1,
                  iv_load_policy: 3,
                  playsinline: 1,
                },
              },
              file: {
                attributes: {
                  playsInline: true,
                },
              },
            }}
          />
          {isPlaying && autoStartMuted && (
            <Icon
              onClick={() => onMute(!isMuted)}
              className={classes.unmuteButton}
              name={isMuted ? "unmute_black" : "mute_black"}
            />
          )}
        </>
      ) : (
        <>
          {videoData?.field_value?.image_video_placeholder?.field_value?.src ? (
            <img
              src={
                videoData.field_value.image_video_placeholder.field_value.src
              }
              alt={
                videoData.field_value.title?.field_value || "Video thumbnail"
              }
              className={classes.thumbnailImg}
            />
          ) : (
            <ReactPlayer
              url={videoData?.field_value?.link_video?.field_value}
              light={true}
              width="100%"
              height="100%"
              className={classes.thumbnailImg}
            />
          )}
          <PlayButton
            className={classes.playButtonOverlay}
            onClick={onPlayClick}
          />
        </>
      )}
    </div>
  )
)

const VideoGalleryV2Module = props => {
  const { title, primary } = props
  const [openSelect, setOpenSelect] = useState(false)
  const [selectIndexValue, setSelectIndexValue] = useState(0)
  const classes = useStyles({ openSelect })

  const [swiperInstance, setSwiperInstance] = useState(null)
  const [indexOfPlay, setIndexOfPlay] = useState(-1)
  const [muteVideo, setMuteVideo] = useState(true)
  const [playVideo, setPlayVideo] = useState(false)
  const [activeIndex, setActiveIndex] = useState(0)
  const [isClient, setIsClient] = useState(false)
  const [autoStartMuted, setAutoStartMuted] = useState(true)

  const navPrevRef = useRef(null)
  const navNextRef = useRef(null)
  const paginationRef = useRef(null)
  const containerRef = useRef(null)

  const { t } = useTranslation()

  useEffect(() => {
    setIsClient(true)
  }, [])

  useEffect(() => {
    if (screenfull.isEnabled) {
      const handleChange = () => {
        if (!screenfull.isFullscreen && containerRef.current) {
          containerRef.current.scrollIntoView()
        }
      }

      if (typeof window !== "undefined") {
        screenfull.on("change", handleChange)
        return () => {
          screenfull.off("change", handleChange)
        }
      }
    }
  }, [])

  const resetVideoState = useCallback(() => {
    setPlayVideo(false)
    setIndexOfPlay(-1)
  }, [])

  const handleTogglePlay = useCallback(() => {
    setPlayVideo(prev => !prev)
  }, [])

  const handleSlideChange = useCallback(
    swiper => {
      resetVideoState()
      const newActiveIndex = swiper.realIndex
      setActiveIndex(newActiveIndex)
      setSelectIndexValue(newActiveIndex)
    },
    [resetVideoState]
  )

  const handleClickPlay = useCallback(
    index => {
      setPlayVideo(true)
      setIndexOfPlay(index)
      const isIOS =
        typeof navigator !== "undefined" &&
        /iPad|iPhone|iPod/.test(navigator.userAgent) &&
        !window.MSStream

      setMuteVideo(isIOS)
      setAutoStartMuted(isIOS)

      if (index !== activeIndex) {
        const videoCount = primary?.field_value?.length || 0
        if (swiperInstance) {
          if (videoCount >= 3) {
            swiperInstance.slideToLoop(index)
          } else {
            swiperInstance.slideTo(index)
          }
        }
      }

      let tracking = {
        eventData: {
          im_collection_name:
            props?.repo?.product_info?.product_universal_title || "",
          im_content:
            primary?.field_value[index]?.field_value?.link_video?.field_value,
        },
      }

      window.snakeAnalytics = window.snakeAnalytics || []
      window.snakeAnalytics.push(["event", "play_video", tracking])
    },
    [activeIndex, primary, swiperInstance]
  )

  const handleChangeSelect = useCallback(
    index => {
      setSelectIndexValue(index)
      if (swiperInstance) {
        const videoCount = primary?.field_value?.length || 0
        if (videoCount >= 3) {
          swiperInstance.slideToLoop(index)
        } else {
          swiperInstance.slideTo(index)
        }
      }
      setOpenSelect(false)
    },
    [primary, swiperInstance]
  )

  const videoCount = primary?.field_value?.length || 0

  if (videoCount === 0) return null

  const swiperParams = {
    modules: [Navigation, Pagination],
    onSwiper: setSwiperInstance,
    onSlideChange: handleSlideChange,
    grabCursor: true,
    watchSlidesProgress: true,
    allowTouchMove: true,
    navigation: isClient
      ? { prevEl: navPrevRef.current, nextEl: navNextRef.current }
      : false,
    pagination: isClient
      ? {
          el: paginationRef.current,
          clickable: true,
          renderBullet: (index, className) =>
            `<span class="${className}"></span>`,
        }
      : false,
    breakpoints: {
      320: { slidesPerView: 1, spaceBetween: 16, centeredSlides: false },
      960: {
        slidesPerView: videoCount < 3 ? videoCount : 3,
        spaceBetween: 16,
        centeredSlides: true,
      },
    },
    loop: videoCount >= 3,
    initialSlide: 0,
  }

  return (
    <Module className={classes.gallery} variant="full" ref={containerRef}>
      <Text
        className={classes.title}
        variant="titleXXXL"
        component="h3"
        align="center"
        weight="bold"
        overline
      >
        {typeof title === "string" ? title : title?.field_value}
      </Text>

      {videoCount > 1 && (
        <div className={classes.selectContainer}>
          <div
            className={classes.select}
            onClick={() => setOpenSelect(!openSelect)}
          >
            <span>
              {
                primary.field_value[selectIndexValue]?.field_value?.title
                  ?.field_value
              }
            </span>
            <p className={classes.selectArrow} />
          </div>
          {openSelect && (
            <div className={classes.selectOptions}>
              {primary.field_value.map((el, i) => (
                <div
                  className={classes.selectOption}
                  key={i}
                  onClick={() => handleChangeSelect(i)}
                >
                  {el.field_value.title.field_value ||
                    `${t("seeVideo")} ${i + 1}`}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className={classes.activeSlideTitle}>
        {primary.field_value[activeIndex]?.field_value?.title?.field_value}
      </div>

      <Box
        className={clsx(classes.container, {
          [classes.singleVideo]: videoCount === 1,
        })}
      >
        {videoCount === 1 ? (
          <VideoPlayerContent
            videoData={primary.field_value[0]}
            isPlaying={indexOfPlay === 0 && playVideo}
            isMuted={muteVideo}
            onPlayClick={() => handleClickPlay(0)}
            onPlay={() => setPlayVideo(true)}
            onPause={() => setPlayVideo(false)}
            onMute={setMuteVideo}
            autoStartMuted={autoStartMuted}
            onTogglePlay={handleTogglePlay}
            classes={classes}
          />
        ) : (
          <>
            {videoCount > 3 && (
              <button
                ref={navPrevRef}
                className={clsx(classes.navButton, classes.prev)}
              >
                <Icon className="icon" name="slider_left_tangerine" />
              </button>
            )}

            <Swiper {...swiperParams} className={classes.swiper}>
              {primary.field_value.map((el, i) => (
                <SwiperSlide key={i} className={classes.slide}>
                  {({ isActive }) => (
                    <VideoPlayerContent
                      videoData={el}
                      isPlaying={indexOfPlay === i && isActive && playVideo}
                      isMuted={muteVideo}
                      onPlayClick={() => handleClickPlay(i)}
                      onPlay={() => setPlayVideo(true)}
                      onPause={() => setPlayVideo(false)}
                      onTogglePlay={handleTogglePlay}
                      onMute={setMuteVideo}
                      autoStartMuted={autoStartMuted}
                      classes={classes}
                    />
                  )}
                </SwiperSlide>
              ))}
            </Swiper>

            {videoCount > 3 && (
              <button
                ref={navNextRef}
                className={clsx(classes.navButton, classes.next)}
              >
                <Icon className="icon" name="slider_right_tangerine" />
              </button>
            )}
          </>
        )}
      </Box>

      {videoCount > 1 && (
        <div ref={paginationRef} className={classes.paginationContainer}></div>
      )}
    </Module>
  )
}

VideoGalleryV2Module.propTypes = {
  title: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      field_value: PropTypes.string,
    }),
  ]),
  primary: PropTypes.shape({
    field_value: PropTypes.arrayOf(
      PropTypes.shape({
        field_value: PropTypes.shape({
          image_video_placeholder: PropTypes.object,
          link_video: PropTypes.object,
          subtitle: PropTypes.object,
          title: PropTypes.object,
        }),
      })
    ),
  }),
}

export default VideoGalleryV2Module
